import React, { useMemo } from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { useChartOfAccountsQuery } from "@api/accountingReports.api";
import { BaseFilterProps } from "../../lib";
import { CoaAccountList } from "../CoaAccountFilter/CoaAccountList";

interface CatalogItemLedgerAccountListProps extends BaseFilterProps {
    selectedIds: string[] | undefined;
    onSelectedIdsChange: (accountIds: string[] | undefined) => void;
    allowedAccountTypes?: AccountType[];
}

// Accounts are entity-scoped, so the same-named account of each business
// entity has its own id. CoaAccountList merges them into one row and
// scopes the tree to the header's entity selection, so ticking a row selects
// that account in every entity in scope.
const CatalogItemLedgerAccountList: React.FC<
    CatalogItemLedgerAccountListProps
> = ({ filters, selectedIds, onSelectedIdsChange, allowedAccountTypes }) => {
    const { data: chartOfAccounts } = useChartOfAccountsQuery();

    const accounts = useMemo(
        () =>
            allowedAccountTypes
                ? chartOfAccounts?.accounts.filter((account) =>
                      allowedAccountTypes.includes(account.type),
                  )
                : chartOfAccounts?.accounts,
        [allowedAccountTypes, chartOfAccounts?.accounts],
    );

    return (
        <CoaAccountList
            accounts={accounts}
            groups={chartOfAccounts?.groups}
            entityIds={filters.entityIds}
            selectedIds={selectedIds}
            onSelectedIdsChange={onSelectedIdsChange}
            onClearAll={() => onSelectedIdsChange(undefined)}
        />
    );
};

export const CatalogItemIncomeAccountFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => (
    <CatalogItemLedgerAccountList
        filters={filters}
        onChange={onChange}
        selectedIds={filters.incomeAccountIds}
        onSelectedIdsChange={(incomeAccountIds) =>
            onChange({ incomeAccountIds })
        }
    />
);

const RECEIVABLES_ACCOUNT_TYPES = [AccountType.accountsReceivable];

export const CatalogItemReceivablesAccountFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => (
    <CatalogItemLedgerAccountList
        filters={filters}
        onChange={onChange}
        selectedIds={filters.receivablesAccountIds}
        onSelectedIdsChange={(receivablesAccountIds) =>
            onChange({ receivablesAccountIds })
        }
        allowedAccountTypes={RECEIVABLES_ACCOUNT_TYPES}
    />
);
